"use client";
import type { Member } from "@zapier/bff";
import {
  Template,
  useAccount,
  useCurrentUser,
  useFetchAssetShares,
} from "@zapier/client";

import {
  AutoTabs,
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  SkeletonText,
  TabsItemType,
  Text,
} from "@zapier/design-system";

import { useTranslations } from "@zapier/i18n";
import { Icon } from "@zapier/zinnia-icons";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useCopyToClipboard, useTabs } from "@zapier/shared";

import { GeneralAccessTabContent } from "./GeneralAccessTabContent";
import { TemplateShareTabContent } from "./TemplateShareTabContent";
import { TemplateShareUpgradeNudge } from "./TemplateShareUpgradeNudge";
import { buildTemplateDetailsPublicUrl } from "./templateDetailsPublicUrl";
import { isTemplateOwnedByCurrentUser } from "./templateUtils";
import styles from "./TemplateShareModal.module.css";

/** Map template hydrated owner to Member for ShareModal (id, fullName, photoUrl). */
function templateOwnerToMember(
  template: Template | null | undefined,
): Member | undefined {
  const owner = template?.owner;
  if (
    !owner ||
    !("id" in owner) ||
    typeof owner.id !== "string" ||
    typeof owner.fullName !== "string"
  ) {
    return undefined;
  }
  const id = Number(owner.id);
  if (!Number.isSafeInteger(id)) return undefined;
  return {
    id,
    fullName: owner.fullName,
    // null required by Member.photoUrl type; avoids empty string → broken <img src="">
    // eslint-disable-next-line unicorn/no-null
    photoUrl: owner.photoUrl ?? null,
    email: "",
    code: "",
    role: "member",
    emailHash: "",
    numTasks: 0,
  };
}

export interface TemplateShareModalProps {
  template: Template | null | undefined;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** When true, show the Workspace tab (current account is a workspace under a parent organization). Default false. */
  showWorkspaceTab?: boolean;
  /** Display name for the workspace tab (e.g. workspace name from BFF). */
  workspaceName?: string;
  /** Display name for the organization tab (e.g. organization name from BFF). */
  organizationName?: string;
  /** When false (e.g. Free/Pro personal), hide Organization tab — only public / General access. Default true. If `showWorkspaceTab` is true, the Organization tab is still hidden (share via Workspace only). */
  showOrganizationTab?: boolean;
  /**
   * When true, render the Organization tab in a locked state with an upgrade
   * nudge in place of the sharing panel. Only applies when the tab would
   * otherwise be hidden for lack of a team plan, so it is ignored under a
   * workspace account. Default false.
   */
  isOrganizationTabUpgradeLocked?: boolean;
}

export function TemplateShareModal({
  template,
  isOpen,
  onOpenChange,
  showWorkspaceTab = false,
  showOrganizationTab: showOrganizationTabProperty = true,
  isOrganizationTabUpgradeLocked = false,
  workspaceName = "Workspace",
  organizationName = "Organization",
}: TemplateShareModalProps) {
  /** Parent-org search + Permissions validation both use the workspace account; hide org picker for workspace contexts. */
  const showOrganizationTab = showOrganizationTabProperty && !showWorkspaceTab;
  const isOrganizationTabLocked =
    isOrganizationTabUpgradeLocked && !showOrganizationTab && !showWorkspaceTab;
  /** The Organization tab renders either way — usable or locked behind the nudge. */
  const hasOrganizationTab = showOrganizationTab || isOrganizationTabLocked;
  const t = useTranslations("template-share-modal");
  const copyToClipboard = useCopyToClipboard();
  const { user } = useCurrentUser();
  const { sharesByType, isLoading: isLoadingShares } = useFetchAssetShares(
    { assetId: template?.id ?? "", assetType: "template" },
    { enabled: Boolean(template?.id) && isOpen },
  );
  const generalAccessSecretId = sharesByType?.url_secret?.find(
    (s) => s.role === "guest_viewer",
  )?.subjectId;
  const assetOwner = useMemo(() => templateOwnerToMember(template), [template]);
  const canShare = isTemplateOwnedByCurrentUser(template, user?.id);
  const { account } = useAccount({
    enabled: isOpen,
  });

  const parentOrgForSharing = useMemo(() => {
    if (account?.parentAccountRelationship !== "workspace") {
      return;
    }
    const parentId = account.parentAccount?.id;
    if (typeof parentId !== "number") {
      return;
    }
    return {
      id: parentId,
      name: account.parentAccount?.name,
    };
  }, [
    account?.parentAccount?.id,
    account?.parentAccount?.name,
    account?.parentAccountRelationship,
  ]);

  const parentOrgAccountId = useMemo(
    () => (parentOrgForSharing ? String(parentOrgForSharing.id) : undefined),
    [parentOrgForSharing],
  );

  const handleCopyTemplateLink = useCallback(() => {
    const url = buildTemplateDetailsPublicUrl(
      globalThis.location.origin,
      template?.slug,
      generalAccessSecretId,
    );
    if (!url) return;
    void copyToClipboard(url);
  }, [copyToClipboard, template?.slug, generalAccessSecretId]);

  const tabs = useMemo(() => {
    const emptyTab = <div data-testid="template-share-tab-no-template" />;

    const workspaceShareTabContent = template?.id ? (
      <TemplateShareTabContent
        assetOwner={assetOwner}
        canShare={canShare}
        templateId={template.id}
        templateName={template.name ?? ""}
      />
    ) : (
      emptyTab
    );

    const organizationShareTabContent = template?.id ? (
      <TemplateShareTabContent
        assetOwner={assetOwner}
        canShare={canShare}
        everyoneOptionAccount={parentOrgForSharing}
        memberSearchAccountId={parentOrgAccountId}
        templateId={template.id}
        templateName={template.name ?? ""}
      />
    ) : (
      emptyTab
    );

    const workspaceTab = showWorkspaceTab
      ? {
          workspace: {
            text: workspaceName,
            body: (
              <div data-testid="template-share-tab-workspace">
                {workspaceShareTabContent}
              </div>
            ),
          },
        }
      : {};

    const organizationTab = showOrganizationTab
      ? {
          organization: {
            text: organizationName,
            body: (
              <div data-testid="template-share-tab-organization">
                {organizationShareTabContent}
              </div>
            ),
          },
        }
      : {};

    const generalAccessTab = {
      generalAccess: {
        text: t("tabs.general-access"),
        body: (
          <div data-testid="template-share-tab-general-access">
            <GeneralAccessTabContent
              canShare={canShare}
              templateId={template?.id ?? ""}
            />
          </div>
        ),
      },
    };

    // General access comes first when the Organization tab is locked so the
    // default landing tab is access the user can actually grant, not the nudge.
    if (isOrganizationTabLocked) {
      return {
        ...generalAccessTab,
        organization: {
          text: t("tabs.teammates"),
          // The tag carries the upgrade signal visually; ariaLabel carries it
          // for screen readers, which don't reach the tag inside a tab link.
          ariaLabel: t("tabs.teammates-locked"),
          tagText: t("tabs.teammates-upgrade-tag"),
          body: (
            <div data-testid="template-share-tab-organization-locked">
              {template?.id ? (
                <TemplateShareUpgradeNudge templateId={template.id} />
              ) : (
                emptyTab
              )}
            </div>
          ),
        },
      };
    }

    return {
      ...workspaceTab,
      ...organizationTab,
      ...generalAccessTab,
    };
  }, [
    showWorkspaceTab,
    showOrganizationTab,
    isOrganizationTabLocked,
    workspaceName,
    organizationName,
    t,
    template?.id,
    template?.name,
    assetOwner,
    canShare,
    parentOrgAccountId,
    parentOrgForSharing,
  ]);

  const firstTabKey = useMemo((): string => {
    if (showWorkspaceTab) return "workspace";
    if (showOrganizationTab) return "organization";
    return "generalAccess";
  }, [showWorkspaceTab, showOrganizationTab]);

  const { activeTabBody, activeTabHref, activeTabKey, tabItems, setActiveTab } =
    useTabs(tabs);
  const hasUserChosenTabRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      // The modal stays mounted between openings, and may reopen for a
      // different template, so a tab choice shouldn't outlive the session that
      // made it — otherwise a locked account reopens straight onto the nudge.
      hasUserChosenTabRef.current = false;
      return;
    }
    const validTabKeys = [
      ...(showWorkspaceTab ? (["workspace"] as const) : []),
      ...(hasOrganizationTab ? (["organization"] as const) : []),
      "generalAccess",
    ];
    if (!validTabKeys.includes(activeTabKey)) {
      setActiveTab(firstTabKey);
      return;
    }
    // `useTabs` freezes its initial tab at first render, which happens on page
    // load — before `account` resolves and before the Organization tab exists.
    // Without this, whether you land on Organization or General access depends
    // on whether the account query was already cached.
    if (!hasUserChosenTabRef.current && activeTabKey !== firstTabKey) {
      setActiveTab(firstTabKey);
    }
  }, [
    isOpen,
    showWorkspaceTab,
    hasOrganizationTab,
    activeTabKey,
    firstTabKey,
    setActiveTab,
  ]);

  const onChooseTab = useCallback(
    (item: TabsItemType) => {
      const hashIndex = item.href.indexOf("#");
      const tabKey = hashIndex === -1 ? "" : item.href.slice(hashIndex + 1);
      if (tabKey) {
        hasUserChosenTabRef.current = true;
        setActiveTab(tabKey);
      }
    },
    [setActiveTab],
  );

  /** Nothing on the nudge panel is shareable, so the copy-link footer has nothing to offer there. */
  const isShowingUpgradeNudge =
    isOrganizationTabLocked && activeTabKey === "organization";

  const title = template?.name
    ? t("title", { template_name: template.name })
    : t("title-untitled");

  return (
    <ModalDialogRoot isOpen={isOpen} onOpenChange={onOpenChange}>
      <ModalDialogHeader>
        <div className={styles.title}>{title}</div>
        <Text
          className={styles.intro}
          color="TextDefault"
          tag="p"
          type="MinimalPrint1Multiline"
        >
          {t(
            showWorkspaceTab || showOrganizationTab
              ? "intro"
              : "intro-public-only",
          )}
        </Text>
        <AutoTabs
          items={tabItems}
          onChooseItem={onChooseTab}
          selectedItemHref={activeTabHref}
        />
      </ModalDialogHeader>
      <ModalDialogBody>
        <div className={styles.contentWrapper}>{activeTabBody}</div>
      </ModalDialogBody>
      {template?.slug && !isShowingUpgradeNudge ? (
        <ModalDialogFooter className={styles.footer}>
          {isLoadingShares ? (
            <SkeletonText lines={1} width="20ch" />
          ) : (
            <Button
              type="button"
              variant="ghost"
              iconPosition="start"
              onPress={handleCopyTemplateLink}
            >
              <Icon name="actionLink" />
              {t("copy-link")}
            </Button>
          )}
        </ModalDialogFooter>
      ) : undefined}
    </ModalDialogRoot>
  );
}
